import { useRef, useState } from "react";
import { Checkbox, Grid, Label, Modal } from "semantic-ui-react";
import { useDispatch, useSelector } from "react-redux";
import ModalWrapper from "../../../app/common/modals/ModalWrapper";
import {
  listenToDocFromFirestore,
  logErrorInFirestore,
  updateCompanyProfileInFirestore,
} from "../../../app/firestore/firestoreService";
import useFirestoreDoc from "../../../app/hooks/useFirestoreDoc";
import LoadingComponent from "../../../app/layout/LoadingComponent";
import { Redirect } from "react-router-dom";
import UxToggle from "../../../app/common/components/UxToggle";
import { Form, Formik } from "formik";
import { COMPANY_PROFILE_FORM_VALIDATION_SCHEMA } from "../../../js/constants/validationSchemas";
import {
  FIRESTORE_COMPANIES_COLLECTION,
  FIRESTORE_COMPANY_PROFILE_COLLECTION,
  FIRESTORE_USERS_COLLECTION,
  LOG_ERROR_CODES,
  QUERY_CODES,
} from "../../../app/firestore/firestoreConstants";
import { CopyToClipboard } from "react-copy-to-clipboard/lib/Component";
import RecruiterTag from "../../employer/components/RecruiterTag";
import { asyncActionError } from "../../../app/async/asyncReducer";
import { reviewService } from "../../../app/api/adminAPI";
import { PAYMENT_COMPANY_PROFILE_SERVICE } from "../../payments/paymentConstants";
import { HR_HEALTHCARE_MODE } from "../../modes/constants";

export default function AdminCompanyReviewModal({ companyProfile }) {
  const dispatch = useDispatch();
  const formRef = useRef();

  const { error } = useSelector((state) => state.async);
  const { currentUserAccount } = useSelector((state) => state.account);

  const [selectedCompanyProfile, setSelectedCompanyProfile] = useState(null);
  const [company, setCompany] = useState(null);
  const [isDetailsLocked, setIsDetailsLocked] = useState(true);
  const [subdomainCheck, setSubdomainCheck] = useState(false);
  const [isApprovingAction, setIsApprovingAction] = useState(false);
  const [employerDetails, setEmployerDetails] = useState(null);

  useFirestoreDoc({
    shouldExecute: employerDetails === null,
    query: () =>
      listenToDocFromFirestore({
        dbCollection: FIRESTORE_USERS_COLLECTION,
        id: companyProfile.employerId,
      }),
    data: (details) => setEmployerDetails(details),
    deps: [companyProfile.employerId],
    queryCode: QUERY_CODES.getUserAccount,
  });

  useFirestoreDoc({
    query: () =>
      listenToDocFromFirestore({
        dbCollection: FIRESTORE_COMPANY_PROFILE_COLLECTION,
        id: companyProfile.id,
      }),
    data: (profile) => setSelectedCompanyProfile(profile),
    deps: [companyProfile.id],
    queryCode: QUERY_CODES.listenToCompanyProfileFromFirestore,
  });

  useFirestoreDoc({
    shouldExecute: company === null,
    query: () =>
      listenToDocFromFirestore({
        dbCollection: FIRESTORE_COMPANIES_COLLECTION,
        id: companyProfile.companyId,
      }),
    data: (details) => setCompany(details),
    deps: [company],
    queryCode: QUERY_CODES.getUserAccount,
  });

  const isSubscription = companyProfile?.isFromSubscription;

  async function handleToggleButton() {
    try {
      setIsApprovingAction(true);
      await reviewService({
        service: PAYMENT_COMPANY_PROFILE_SERVICE,
        id: companyProfile.id,
        isSubscription,
      });
    } catch (e) {
      console.log(e);
      await logErrorInFirestore({
        error: e.toString(),
        errorCode: LOG_ERROR_CODES.adminReviewCompanyProfile,
        errorPage: "Admin job review company modal - toggle",
        notes: {
          employerId: employerDetails.id,
          companyId: companyProfile.id,
          companyRefName: companyProfile.refName,
          companyIsInitialised: companyProfile.isInitialised,
        },
      });
      dispatch(
        asyncActionError({
          code: "approve-job-error",
          message: "Could not approve company",
        })
      );
    } finally {
      setIsApprovingAction(false);
    }
  }

  // SHOW company in <a> tag</a> privacyPolicyLink so check opens correctly in new tab

  if (
    !companyProfile ||
    !selectedCompanyProfile ||
    !employerDetails ||
    !company
  )
    return <LoadingComponent />;
  if (error) return <Redirect to="/error" />;

  const isCompanyId =
    companyProfile?.companyId === employerDetails?.companyId &&
    employerDetails?.companyId === company?.id;

  console.log("***");
  console.log("Employer details", employerDetails);
  console.log("Company", company);
  console.log("Company Profile", selectedCompanyProfile);

  return (
    <ModalWrapper
      modalClassName={"modal-ux-wrapper"}
      centered={false}
      closeOnEscape={false}
      closeOnDimmerClick={false}
      padding={18}
    >
      <Grid>
        <Grid.Row>
          <Grid.Column width={16}>
            <h2>
              Review Company{" "}
              <span className="body-blue-text">(Id: {companyProfile.id})</span>{" "}
              <span className="body-blue-text">
                (Ref: {companyProfile.refName})
              </span>{" "}
              <span
                className={
                  companyProfile.mode === HR_HEALTHCARE_MODE
                    ? "healthcare-background body-blue-text"
                    : "body-blue-text"
                }
              >
                {companyProfile.mode}
              </span>
            </h2>
            <hr className="solid-divider" />
            <div className="body-blue-text">
              <div>
                <span className="body-bold-text">Employer details: </span>
                <span>{employerDetails.displayName} / </span>
                <CopyToClipboard text={employerDetails.email}>
                  <span className="clickable">{employerDetails.email} / </span>
                </CopyToClipboard>{" "}
                <span>{employerDetails.sibId}</span>
              </div>
              <div style={{ marginTop: 4 }}>
                <span className="body-bold-text">Initialised: </span>
                <span>
                  {selectedCompanyProfile.isInitialised ? "Yes" : "No"}
                </span>
              </div>
              <div style={{ marginTop: 4 }}>
                <span className="body-bold-text">Paid: </span>
                <span>
                  {companyProfile.isPaidFor ? "Yes" : "No"}{" "}
                  {companyProfile.isFree && " - Free"}
                </span>
              </div>
              <div style={{ marginTop: 4 }}>
                <span className="body-bold-text">Posted on subscription: </span>
                <span>{isSubscription ? "Yes" : "No"}</span>
              </div>
              <div style={{ marginTop: 4 }}>
                <span className="body-bold-text">Time posted: </span>
                <span>{companyProfile.dateCreated.toString()}</span>
              </div>
              {employerDetails.isRecruiter && <RecruiterTag inline={false} />}

              <div style={{ marginTop: 4 }}>
                <span className="body-bold-text">
                  Company Id check {isCompanyId ? "PASS" : "FAIL"}:{" "}
                </span>
                <span style={{ color: isCompanyId ? "green" : "red" }}>
                  Company - {company?.id} | Employer -{" "}
                  {employerDetails.companyId}
                </span>
              </div>
              <div style={{ marginTop: 4 }}>
                <span className="body-bold-text">Hosted careers page: </span>
                <span
                  style={{
                    color: selectedCompanyProfile?.subdomain ? "green" : "red",
                    marginRight: 10,
                  }}
                >
                  {selectedCompanyProfile?.subdomain || "N/A"}{" "}
                  {selectedCompanyProfile?.isHostedPage ? "ON" : "OFF"}
                </span>
                {selectedCompanyProfile?.subdomain && (
                  <span>
                    <a
                      rel="noreferrer"
                      href={`https://${selectedCompanyProfile?.subdomain}.helloradius.com`}
                      target="_blank"
                    >
                      {`${selectedCompanyProfile?.subdomain}.helloradius.com`}
                    </a>
                  </span>
                )}
                <span style={{ margin: "0 10px" }}>
                  - Check on Vercel domains -
                </span>
                <span
                  style={{
                    border: "1px solid orange",
                    padding: 5,
                    borderRadius: 10,
                  }}
                >
                  <Checkbox
                    checked={subdomainCheck}
                    label={"Domain checked"}
                    style={{ marginRight: 2 }}
                    onChange={(e, { checked }) => setSubdomainCheck(checked)}
                  />
                </span>
              </div>
            </div>
            <Modal.Content
              scrolling
              style={{
                marginTop: 15,
                paddingTop: 10,
                borderTop: "1px solid #8d9cab",
                height: "50vh",
              }}
            >
              <div>
                <Formik
                  initialValues={companyProfile}
                  validationSchema={COMPANY_PROFILE_FORM_VALIDATION_SCHEMA}
                  innerRef={formRef}
                  onSubmit={async (values, { setSubmitting, setErrors }) => {
                    try {
                      setIsDetailsLocked(true);
                      await updateCompanyProfileInFirestore({
                        company: values,
                        isAdmin: currentUserAccount.isAdmin,
                      });
                    } catch (e) {
                      setErrors({ message: e.message });
                    } finally {
                      setSubmitting(false);
                    }
                  }}
                >
                  {({ isSubmitting, isValid, errors, values }) => (
                    <Form className="ui form" style={{ marginRight: 20 }}>
                      <Checkbox
                        checked={isDetailsLocked}
                        label="Details locked"
                        style={{ marginRight: 10, paddingTop: 2 }}
                        onChange={(e, { checked }) =>
                          setIsDetailsLocked(checked)
                        }
                      />
                      <div style={{ margin: "10px 0" }}>
                        {selectedCompanyProfile.id}
                      </div>

                      <div
                        style={{
                          padding: "16px",
                          border: "1px solid #ddd",
                          borderRadius: "8px",
                          background: "#fff",
                          width: "fit-content",
                        }}
                      >
                        <h3
                          style={{
                            fontSize: "18px",
                            fontWeight: "bold",
                            marginBottom: "8px",
                          }}
                        >
                          User Details
                        </h3>
                        <ul
                          style={{ listStyle: "none", padding: 0, margin: 0 }}
                        >
                          {Object.entries(selectedCompanyProfile).map(
                            ([key, value]) => (
                              <li key={key} style={{ marginBottom: "4px" }}>
                                <span
                                  style={{
                                    fontWeight: "bold",
                                    textTransform: "capitalize",
                                  }}
                                >
                                  {key}:
                                </span>{" "}
                                {/* Handle different data types */}
                                {typeof value === "object" && value !== null ? (
                                  <pre
                                    style={{
                                      display: "inline",
                                      margin: 0,
                                      whiteSpace: "pre-wrap",
                                    }}
                                  >
                                    {JSON.stringify(value, null, 2)}
                                  </pre>
                                ) : typeof value === "boolean" ? (
                                  value ? (
                                    "✅ Yes"
                                  ) : (
                                    "❌ No"
                                  )
                                ) : (
                                  value
                                )}
                              </li>
                            )
                          )}
                        </ul>
                      </div>
                    </Form>
                  )}
                </Formik>
                <br />
              </div>
            </Modal.Content>
          </Grid.Column>
        </Grid.Row>
        <Grid.Row>
          <Grid.Column width={11}>
            <div>
              If location was updated,{" "}
              <span className="semi-bold">Save & update</span> first before
              approving | Check domain
            </div>
          </Grid.Column>
          <Grid.Column textAlign="right" width={5}>
            {isApprovingAction && (
              <div style={{ float: "right", marginRight: 20 }}>
                <Label basic color="grey" content="Waiting..." />
              </div>
            )}
            {selectedCompanyProfile && (
              <UxToggle
                labelText={
                  selectedCompanyProfile.isApproved ? "Approved" : "Approve"
                }
                handleClick={handleToggleButton}
                checked={selectedCompanyProfile.isApproved}
                isDisabled={
                  !subdomainCheck ||
                  isApprovingAction ||
                  !selectedCompanyProfile ||
                  selectedCompanyProfile.isClosed ||
                  !isCompanyId
                }
              />
            )}
          </Grid.Column>
        </Grid.Row>
      </Grid>
    </ModalWrapper>
  );
}
